import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  inventoryApi,
  type InventoryListParams,
  type MovementListParams,
  type CreateMovementInput,
} from "@/lib/admin-api/inventory";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const INVENTORY_KEY = "admin-inventory" as const;
const MOVEMENTS_KEY = "admin-inventory-movements" as const;

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Fetch paginated list of products with current stock levels. */
export function useInventoryStock(params: InventoryListParams = {}) {
  return useQuery({
    queryKey: [INVENTORY_KEY, params],
    queryFn: () => inventoryApi.stock.list(params),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

/** Fetch paginated movement history, optionally scoped to a product. */
export function useStockMovements(params: MovementListParams = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [MOVEMENTS_KEY, params],
    queryFn: () => inventoryApi.stock.movements(params),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
    enabled: options.enabled ?? true,
  });
}

/** Create a stock movement and invalidate related caches. */
export function useCreateMovement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateMovementInput) =>
      inventoryApi.stock.createMovement(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVENTORY_KEY] });
      queryClient.invalidateQueries({ queryKey: [MOVEMENTS_KEY] });
    },
  });
}
